import { CalendarIcon } from "@xola/icons";
import { DatePicker } from "@xola/ui-kit";
import { IFilterParams } from "ag-grid-enterprise";
import { convertAnyStringToFormattedTime, DateFormat, formatDate, JsDateRange, now } from "common";
import dayjs from "dayjs";
import { isEmpty } from "lodash";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { ReportObject } from "types";
import shallow from "zustand/shallow";
import { useSeller } from "../../../../hooks/useAccount";
import { getFormattedFilterModel, getReportFilterState } from "../CustomReportGrid";
import { ReportFilter, ReportFilterOperator, ReportFilterState } from "../ReportFilterState";
import { ReportColumnDef, ReportColumnFilterValueType } from "../SuggestedReports";
import { formatCreatedAtEndDate } from "@/models/Report";
import { useCustomReportStore } from "@/stores/customReportFilterStore";

export const DateRangeFilter = forwardRef((props: IFilterParams, ref) => {
    const report = props.colDef.filterParams?.report;
    const field = props.colDef.field;
    const fieldName = field ?? "createdAt";

    const { timezoneName } = useSeller();
    const [initialFilter, filter, currentFilter, setCurrentFilter] = useCustomReportStore(
        (state) => [state.initialFilter, state.currentFilter, state.currentFilter, state.setCurrentFilter],
        shallow,
    );
    const filterValue = currentFilter[fieldName] as { value: (string | number)[] };
    const [value, setValue] = useState<JsDateRange>({
        from: new Date(filterValue?.value[0] ?? Date.now()),
        to: filterValue?.value[1] ? new Date(filterValue.value[1]) : null,
    });

    const { filterValueType } = props.colDef as ReportColumnDef;

    useImperativeHandle(ref, () => ({
        isFilterActive() {
            return value.from && value.to;
        },

        doesFilterPass() {
            return true; // Disables client-side filtering
        },

        getModel(): ReportFilter | null {
            if (value.from && value.to) {
                return {
                    operator: ReportFilterOperator.RANGE,
                    value: getFilterValue(value, filterValueType) ?? [],
                };
            }

            return null;
        },

        setModel(filter?: ReportFilter) {
            if (filter && Array.isArray(filter.value)) {
                setValue(getDateRange(filter.value));
            }
        },
    }));

    const handleFilterChange = (filter: ReportFilterState) => {
        const localFilter = { ...filter };

        if (localFilter.product?.operator === ReportFilterOperator.IN && isEmpty(localFilter.product?.value)) {
            // Hack for removing "product[in]=[]" from the query params because Rushi can't figure out how to do it
            delete localFilter.product;
        }

        if (localFilter.partner?.operator === ReportFilterOperator.IN && isEmpty(localFilter.partner?.value)) {
            delete localFilter.partner;
        }

        if (
            localFilter["items.product"]?.operator === ReportFilterOperator.IN &&
            isEmpty(localFilter["items.product"]?.value)
        ) {
            delete localFilter["items.product"];
        }

        if (
            localFilter["lineItems.coupon"]?.operator === ReportFilterOperator.IN &&
            isEmpty(localFilter["lineItems.coupon"]?.value)
        ) {
            delete localFilter["lineItems.coupon"];
        }

        if (report.object === ReportObject.TICKET_REDEMPTION && localFilter.createdAt) {
            localFilter.createdAt.value = formatCreatedAtEndDate(localFilter.createdAt.value as string[]);
        }

        if (localFilter?.arrivalTime) {
            localFilter.arrivalTime = {
                ...localFilter.arrivalTime,
                value: convertAnyStringToFormattedTime((localFilter?.arrivalTime.value as string).toString()), // TODO: FIX ME: I think we can just use `now()` here
            };
        }

        if (
            report.object === ReportObject.RESERVATIONS &&
            localFilter.status &&
            localFilter.status.operator === ReportFilterOperator.NIN
        ) {
            // Old saved reservations reports has NIN filter, now it is changed to IN filter, so removed old NIN
            delete localFilter.status;
        }

        if (report.object === ReportObject.RESERVATIONS_BY_LISTING && localFilter.arrivalDate) {
            // Filter arrival date by Item level also
            localFilter.items_arrivalDate = localFilter.arrivalDate;
        }

        setCurrentFilter(localFilter);
    };

    const handleDateChange = (value: JsDateRange) => {
        setValue(value);
    };

    const handleSubmit = () => {
        const model = {
            operator: ReportFilterOperator.RANGE,
            value: getFilterValue(value, filterValueType) ?? [],
        };
        const formattedFilterModel = getFormattedFilterModel({ [fieldName]: model }, filter, initialFilter, report);
        handleFilterChange(getReportFilterState(formattedFilterModel));
        props.api.refreshHeader();
    };

    return (
        <div>
            <DatePicker
                shouldShowRelativeRanges
                variant="range"
                value={value}
                timezoneName={timezoneName}
                onChange={handleDateChange}
                onSubmitDateRange={handleSubmit}
            >
                <div className="ag-custom-component-popup flex h-10 w-full cursor-pointer items-center rounded border border-gray-light p-3">
                    <CalendarIcon className="mr-1" size="medium" />

                    <span className="ag-custom-component-popup truncate">
                        {value.from ? formatDate(value.from, DateFormat.DATE) : "Pending"}
                        &nbsp;-&nbsp;
                        {value.to ? formatDate(value.to, DateFormat.DATE) : "Pending"}
                    </span>
                </div>
            </DatePicker>
        </div>
    );
});

const getFilterValue = (value: JsDateRange, filterValueType?: ReportColumnFilterValueType) => {
    if (filterValueType === ReportColumnFilterValueType.TIMESTAMP) {
        return [dayjs(value.from).unix(), dayjs(value.to).unix()];
    }

    return [formatDate(value.from ?? now(), DateFormat.DATE_ISO), formatDate(value.to ?? now(), DateFormat.DATE_ISO)];
};

const getDateRange = ([from, to]: any[]): JsDateRange => {
    return { from: parseDate(from), to: parseDate(to) };
};

const parseDate = (value: any) => {
    if (dayjs(value, DateFormat.DATE_ISO, true).isValid()) {
        return dayjs(value).toDate();
    }

    const timestamp = Number(value);

    if (timestamp > 0) {
        return dayjs(value, "X").toDate();
    }

    return now().toDate();
};
